<template>
  <div class="add-property">
    <div class="content-wrapper">
      <!-- Form Section -->
      <div class="form-container">
        <h1>Add Property</h1>
        <form @submit.prevent="submitProperty">
          <div class="form-group">
            <label for="displayName">Display Name</label>
            <input
              type="text"
              id="displayName"
              v-model="property.displayName"
              required
            />
          </div>

          <!-- Landlord Selector -->
          <div class="form-group">
            <label for="landlord">Select Landlord</label>
            <LandlordSelector
              :properties="landlordList"
              :selected-property="property.selectedLandlord"
              @property-selected="updateSelectedLandlord"
            />
          </div>

          <div class="form-group">
            <label for="address">Address</label>
            <div class="address-fields">
              <input
                type="text"
                placeholder="Building No. & Name"
                v-model="property.address.building"
                required
              />
              <input
                type="text"
                placeholder="Street Name"
                v-model="property.address.street"
                required
              />
              <input
                type="text"
                placeholder="Suburb"
                v-model="property.address.suburb"
                required
              />
              <input
                type="text"
                placeholder="City"
                v-model="property.address.city"
                required
              />
              <input
                type="text"
                placeholder="Postal Code"
                v-model="property.address.postalCode"
                required
              />
            </div>
          </div>

          <div class="form-group">
            <label for="category">Category</label>
            <select id="category" v-model="property.category" required>
              <option disabled value="">Select Category</option>
              <option value="residential">Residential</option>
              <option value="residential">Commercial</option>
              

            </select>
          </div>

          <div class="form-group">
            <label for="type">Type</label>
            <select id="type" v-model="property.type" required>
              <option disabled value="">Select Type</option>
              <option value="house">House</option>
              <option value="apartment">Apartment</option>
            </select>
          </div>

          

          <div class="form-group">
            <label for="vacant">Is Vacant</label>
            <input
              type="checkbox"
              id="vacant"
              v-model="property.isVacant"
              required
            />
            <span>{{ property.isVacant ? "Yes" : "No" }}</span>
          </div>

          <div class="form-group">
            <label for="location">Location</label>
            <p>Latitude: {{ property.location.lat }}</p>
            <p>Longitude: {{ property.location.lng }}</p>
          </div>

          <button type="submit">Add Property</button>
        </form>
      </div>

      <!-- Map Section -->
      <div class="map-container">
        <Map @locationChanged="updateLocation" />
      </div>
    </div>
  </div>
</template>

<script>
import Map from "./Map.vue";
import LandlordSelector from "./LandlordSelector.vue";
import {
  addDoc,
  collection,
  getFirestore,
  query,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";

const db = getFirestore();
const auth = getAuth();

export default {
  name: "AddProperty",
  components: {
    Map,
    LandlordSelector,
  },
  data() {
    return {
      property: {
        displayName: "",
        address: {
          building: "",
          street: "",
          suburb: "",
          city: "",
          postalCode: "",
        },
        category: "",
        type: "",
        bankAccount: "",
        location: { lat: 0, lng: 0 },
        reference: "",
        isVacant: false,
        selectedLandlord: null,
        userUid: "",
      },
      landlordList: [], // List of landlords from Firestore
      loading: false,
    };
  },
  methods: {
    async fetchLandlords() {
      try {
        const querySnapshot = await getDocs(collection(db, "landlords"));
        this.landlordList = querySnapshot.docs.map((doc) => ({
          id: doc.reference,
          ...doc.data(),
        }));
      } catch (error) {
        console.error("Error fetching landlords:", error);
      }
    },
    updateSelectedLandlord(landlord) {
      this.property.selectedLandlord = landlord;
    },
    updateLocation(location) {
      this.property.location = location;
    },
    async submitProperty() {
      if (this.loading) return;

      this.loading = true;

      try {
        const user = auth.currentUser;
        if (!user) {
          alert("You must be logged in to add a property.");
          this.loading = false;
          return;
        }

        this.property.userUid = user.uid;

        // Generate property reference
        const propertyRefQuery = query(
          collection(db, "properties"),
          orderBy("reference", "desc"),
          limit(1)
        );
        const querySnapshot = await getDocs(propertyRefQuery);

        let newRef = "PRO000001";
        if (!querySnapshot.empty) {
          const lastProperty = querySnapshot.docs[0].data();
          const lastNum = parseInt(lastProperty.reference.replace("PRO", ""));
          newRef = "PRO" + (lastNum + 1).toString().padStart(6, "0");
        }

        this.property.reference = newRef;

        // Prepare property data for saving
        const propertyData = {
          ...this.property,
          selectedLandlord: this.property.selectedLandlord?.reference
          || null, // Save landlord ID
        };

        await addDoc(collection(db, "properties"), propertyData);

        alert("Property added successfully!");
        this.$router.push({ path: "/properties", query: { type: "Properties" } });

        // Reset form
        this.property = {
          displayName: "",
          address: {
            building: "",
            street: "",
            suburb: "",
            city: "",
            postalCode: "",
          },
          category: "",
          type: "",
          bankAccount: "",
          location: { lat: 0, lng: 0 },
          reference: "",
          isVacant: false,
          selectedLandlord: null,
          userUid: "",
        };
      } catch (error) {
        console.error("Error adding property:", error);
        alert("An error occurred while adding the property.");
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.fetchLandlords();
  },
};
</script>

<style scoped>
.add-property {
  padding: 20px;
  background: #f9f9f9;
  font-family: Arial, sans-serif;
}

.content-wrapper {
  display: flex;
  gap: 20px;
}

.form-container {
  flex: 1;
  background: #ffffff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

h1 {
  text-align: center;
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

.form-group {
  margin-bottom: 15px;
}

label {
  display: block;
  font-weight: bold;
  margin-bottom: 5px;
  color: #555;
}

input[type="text"],
select {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

button[type="submit"] {
  display: block;
  width: 100%;
  padding: 12px;
  font-size: 16px;
  color: #fff;
  background-color: #007bff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.map-container {
  flex: 1;
  height: 400px;
  background: #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}
</style> 

